import { useState } from "react";
import { toast } from "sonner";
import { Copy, Scroll } from "@phosphor-icons/react";
import { api, errMsg, LANGS, COUNTRIES } from "@/lib/api";
import { PageHead } from "@/components/Layout";
import { EmailGate } from "@/components/EmailGate";
import { useT } from "@/i18n";

const init = { property_type: "apartment", country: "Spain", lang: "English", checkin_time: "15:00", checkout_time: "11:00", quiet_hours: "22:00-08:00", pets: false, smoking: false, max_guests: 4, extra: "" };

export default function HouseRulesPage() {
  const { t, lang } = useT();
  const [f, setF] = useState({ ...init, ...(lang === "it" ? { lang: "Italian", country: "Italy" } : {}) });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [full, setFull] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const run = async (e) => {
    e.preventDefault();
    setBusy(true); setPreview(null); setFull(null);
    try {
      const { data } = await api.post("/tools/house-rules", { ...f, max_guests: Number(f.max_guests) });
      setPreview(data);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="house-rules-page">
      <PageHead eyebrow="Free tool" title="House Rules Generator" sub="Friendly, clear rules guests actually follow. In 10 languages, in under a minute." />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24 grid lg:grid-cols-[380px_1fr] gap-8 items-start">
        <form onSubmit={run} className="card p-6 bg-white grid gap-4" data-testid="house-rules-form">
          <div className="grid grid-cols-2 gap-3">
            <div><span className="label">{t("Property")}</span>
              <select className="field" value={f.property_type} onChange={set("property_type")} data-testid="hr-type-select">
                {["apartment", "house", "villa", "studio", "room", "cottage"].map((o) => <option key={o} value={o}>{t(o)}</option>)}
              </select></div>
            <div><span className="label">{t("Country")}</span>
              <select className="field" value={f.country} onChange={set("country")} data-testid="hr-country-select">{COUNTRIES.map((o) => <option key={o} value={o}>{t(o)}</option>)}</select></div>
          </div>
          <div><span className="label">{t("Language")}</span>
            <select className="field" value={f.lang} onChange={set("lang")} data-testid="hr-lang-select">{LANGS.map((o) => <option key={o} value={o}>{t(o)}</option>)}</select></div>
          <div className="grid grid-cols-3 gap-3">
            <div><span className="label">{t("Check-in")}</span><input className="field" value={f.checkin_time} onChange={set("checkin_time")} data-testid="hr-checkin-input" /></div>
            <div><span className="label">{t("Check-out")}</span><input className="field" value={f.checkout_time} onChange={set("checkout_time")} data-testid="hr-checkout-input" /></div>
            <div><span className="label">{t("Guests")}</span><input type="number" min="1" max="30" className="field" value={f.max_guests} onChange={set("max_guests")} data-testid="hr-guests-input" /></div>
          </div>
          <div><span className="label">{t("Quiet hours")}</span><input className="field" value={f.quiet_hours} onChange={set("quiet_hours")} data-testid="hr-quiet-input" /></div>
          <div className="flex gap-6 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.pets} onChange={set("pets")} className="accent-[#0F4C5C]" data-testid="hr-pets-checkbox" /> {t("Pets allowed")}</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.smoking} onChange={set("smoking")} className="accent-[#0F4C5C]" data-testid="hr-smoking-checkbox" /> {t("Smoking outside")}</label>
          </div>
          <div><span className="label">{t("Anything else")}</span><textarea rows={3} className="field resize-none" value={f.extra} onChange={set("extra")} placeholder={t("Shoes off, pool closes at 20:00…")} data-testid="hr-extra-input" /></div>
          <button disabled={busy} className="btn-coral justify-center" data-testid="hr-generate-btn"><Scroll size={18} weight="fill" /> {busy ? t("Writing…") : t("Generate house rules")}</button>
        </form>

        <div className="card p-8 min-h-[420px] bg-[#FCFAF8]" data-testid="house-rules-output">
          {!preview && !busy && <p className="text-gray-500">{t("Your house rules will appear here.")}</p>}
          {busy && <div className="space-y-3">{[90, 80, 70, 85, 60].map((w) => <div key={w} className="skeleton h-4" style={{ width: `${w}%` }} />)}</div>}
          {preview && !full && (
            <div className="rise">
              <div className="space-y-2 text-gray-800" data-testid="hr-preview">{preview.preview.map((l, i) => <p key={i}>{l}</p>)}</div>
              <div className="relative mt-2 h-16 bg-gradient-to-b from-transparent to-[#FCFAF8]" />
              <p className="text-sm text-gray-600 mb-4">{t("+ {n} more lines", { n: Math.max(preview.total_lines - preview.preview.length, 0) })}</p>
              <EmailGate reportId={preview.report_id} onUnlock={setFull} cta="Show all rules" />
            </div>
          )}
          {full && (
            <div className="rise">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-2xl text-[#0F4C5C]">{t("Your house rules")}</h3>
                <button onClick={() => { navigator.clipboard.writeText(full.text); toast.success(t("Copied")); }} className="btn-ghost text-sm !py-1.5" data-testid="hr-copy-btn"><Copy size={16} /> {t("Copy")}</button>
              </div>
              <pre className="whitespace-pre-wrap font-sans text-gray-800 leading-relaxed" data-testid="hr-full-text">{full.text}</pre>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
